import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Search, Users, ChevronRight, KeyRound } from "lucide-react";
import { api, ageFrom, fmtDate } from "@/lib/api";
import { EmptyState } from "@/components/Field";
import { PatientFormDialog } from "@/components/PatientFormDialog";

export default function Patients() {
  const [list, setList] = useState(null);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    const t = setTimeout(() => api.get("/patients", { params: { q } }).then((r) => setList(r.data)), 250);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <div className="fade-up space-y-8" data-testid="patients-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-xs text-[#3d655b]">Anagrafica</div>
          <h1 className="page-title mt-1">Pazienti</h1>
        </div>
        <button className="btn-primary" onClick={() => setOpen(true)} data-testid="new-patient-button"><Plus className="h-4 w-4" /> Nuovo paziente</button>
      </div>
      <div className="relative max-w-md">
        <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <input className="field rounded-full pl-11" placeholder="Cerca per nome, email o codice fiscale…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="patients-search-input" />
      </div>
      {list && list.length === 0 ? (
        <EmptyState icon={Users} title="Nessun paziente trovato" text="Crea la prima scheda paziente per iniziare." testid="patients-empty">
          <button className="btn-primary" onClick={() => setOpen(true)}><Plus className="h-4 w-4" /> Nuovo paziente</button>
        </EmptyState>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-[#e2e8f0] bg-white shadow-sm">
          <table className="w-full text-left text-sm" data-testid="patients-table">
            <thead className="bg-[#f8fafc]">
              <tr>
                <th className="label-xs px-5 py-3">Paziente</th>
                <th className="label-xs hidden px-5 py-3 md:table-cell">Età</th>
                <th className="label-xs hidden px-5 py-3 md:table-cell">Ultima visita</th>
                <th className="label-xs hidden px-5 py-3 sm:table-cell">Peso</th>
                <th className="label-xs hidden px-5 py-3 lg:table-cell">Accesso</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody>
              {(list || []).map((p) => (
                <tr key={p.id} onClick={() => nav(`/pazienti/${p.id}`)} className="cursor-pointer border-t border-[#e2e8f0] transition-colors hover:bg-[#edf2ef]/40" data-testid={`patient-row-${p.id}`}>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-3">
                      <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-[#edf2ef] text-xs font-bold text-[#2f4f47]">{p.first_name[0]}{p.last_name[0]}</div>
                      <div className="min-w-0">
                        <Link to={`/pazienti/${p.id}`} className="font-semibold">{p.last_name} {p.first_name}</Link>
                        <div className="truncate text-xs text-slate-500">{p.email || p.phone || "—"}</div>
                      </div>
                    </div>
                  </td>
                  <td className="hidden px-5 py-4 md:table-cell">{ageFrom(p.birth_date) ?? "—"}</td>
                  <td className="hidden px-5 py-4 md:table-cell">{fmtDate(p.last_visit?.date)}</td>
                  <td className="hidden px-5 py-4 font-semibold sm:table-cell">{p.last_visit?.weight ? `${p.last_visit.weight} kg` : "—"}</td>
                  <td className="hidden px-5 py-4 lg:table-cell">
                    {p.user_id ? <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700"><KeyRound className="h-3 w-3" /> Attivo</span> : <span className="text-xs text-slate-400">Nessuno</span>}
                  </td>
                  <td className="px-5 py-4 text-right"><ChevronRight className="ml-auto h-4 w-4 text-slate-400" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <PatientFormDialog open={open} onOpenChange={setOpen} onCreated={(p) => nav(`/pazienti/${p.id}`)} />
    </div>
  );
}
